/**
 * ledgerTransactionId
 *
 * The deterministic transaction id for every posting source, which is the ledger's idempotency key:
 * a second post of the same id changes nothing. Forms: `acc:{ruleId}:{key}`,
 * `sig:{channelId}:{invoice}:{lineItemNum}:{deliveryNumber}`, `file:{fileHash}` (a statement file's receipt,
 * minted from a null `index` and `importNumber` 1), `file:{fileHash}:import:{importNumber}` (its receipt when imported
 * again, each earlier import reversed) and `file:{fileHash}:row:{index}` (one of its statement rows, cited in provenance),
 * `man:{uid}:{entryKey}`, `alloc:{proposalId}:{claimId}`, `adj:{proposalId}:{targetId}` (an adjustment a
 * proposal's commit posts, at most one per target), `close:{periodKey}:{closeNumber}` (a period's close, numbered
 * from 1 so a reopened period can close again) and `rev:{reversesTransactionId}`. A manual
 * entry key is minted by the client once per entry, so a retried call replays rather than posting
 * twice. Ids carry no `ledgerId`, because the primary key is `(ledger_id, transaction_id)`. A reversal's id is a function
 * of its target alone, so the primary key is what lets a transaction be reversed at most once. Every
 * segment except the last must be `:`-free, or this throws; the last may hold colons, which is what
 * lets an allocation or proposal adjustment id end in a claim or payment id and a reversal id end in any
 * transaction id.
 */

import type { LedgerTransactionIdSource } from '../System.Ledger.Definition.types';
import { ledgerSourceLineKey } from './ledgerSourceLineKey';

function segmentsOf(source: LedgerTransactionIdSource): string[] {
    switch (source.source) {
        case 'accrual':
            return ['acc', source.ruleId, ledgerSourceLineKey(source.sourceLine, source.grainRef)];
        case 'signify':
            return ['sig', source.channelId, source.invoice, source.lineItemNum, source.deliveryNumber];
        case 'file':
            if (source.index !== null) return ['file', source.fileHash, 'row', String(source.index)];
            return source.importNumber === 1 ? ['file', source.fileHash] : ['file', source.fileHash, 'import', String(source.importNumber)];
        case 'manual':
            return ['man', source.uid, source.entryKey];
        case 'allocation':
            return ['alloc', source.proposalId, source.claimId];
        case 'proposalAdjustment':
            return ['adj', source.proposalId, source.targetId];
        case 'reversal':
            return ['rev', source.reversesTransactionId];
        case 'periodClose':
            return ['close', source.periodKey, String(source.closeNumber)];
    }
}

function ledgerTransactionId(source: LedgerTransactionIdSource): string {
    const segments = segmentsOf(source);
    segments.slice(0, -1).forEach((segment) => {
        if (segment.includes(':')) {
            throw new Error(`ledgerTransactionId: segment "${segment}" of a ${source.source} id contains ":"`);
        }
    });
    return segments.join(':');
}

export { ledgerTransactionId };
